<template>
    <BaseDialog v-bind="$attrs" :title="!userDetails.has_calendar_integration ? 'Calendar Connection' : 'Calendar Settings'">
        <div class="flex flex-col gap-12 justify-start items-start py-6 md:py-15 px-4 md:px-10 md:min-w-125 min-h-130">
            <div class="w-full">
                <div class="flex flex-row justify-between items-center w-full">
                    <div class="flex flex-row gap-2.5">
                        <i class="bi bi-calendar" aria-hidden="true"></i>
                        <h3 class="title-subsection">{{ !userDetails.has_calendar_integration ? "Calendar Connection" : "Calendar Settings" }}</h3>
                    </div>
                    <BaseButton v-if="tonalThemeEnabled" theme="tertiary" size="sm" icon-only aria-label="Close calendar settings" @click="closeDialog()">
                        <i class="bi bi-x text-xl" aria-hidden="true"></i>
                    </BaseButton>
                    <BaseButton v-else theme="tertiary" class="p-2!" aria-label="Close calendar settings" @click="closeDialog()">
                        <i class="bi bi-x text-3xl text-[#939292]" aria-hidden="true"></i>
                    </BaseButton>
                </div>
                <p v-if="!userDetails.has_calendar_integration" class="body-small">Personalized help for meetings, presentations, and important events — right in chat.</p>
            </div>
            <div class="flex flex-col gap-2.5 w-full">
                <div class="body-small">Time Zone</div>
                <BaseCombobox v-model:="selectedTimezone" :options="calendarIntegration.timezones.map((timezone) => ({ label: timezone, value: timezone }))"> </BaseCombobox>
                <div class="flex flex-col gap-2.5 w-full">
                    <div class="body-small">
                        Connection
                        <span v-if="!userDetails.has_calendar_integration" class="ml-2 body-small text-valence-grey-750"> You can disconnect at any time in settings. </span>
                    </div>
                    <CalendarIntegrationDisconnect @close="closeDialog" />
                </div>
            </div>
            <div v-if="!!calendarIntegration?.private_toggle_enabled" class="flex flex-col gap-2 w-full">
                <p v-if="!userDetails.has_calendar_integration" class="body-small text-valence-grey-750">By default, your private events will not be shared with the AI Coach</p>

                <label class="body-small flex items-center gap-2" for="private-event">
                    Include private events
                    <BaseSwitch id="private-event" v-model="allowPrivateEvents" size="sm" />
                </label>
            </div>
            <div class="flex flex-col justify-center items-center w-full">
                <BaseButton
                    v-if="!userDetails.has_calendar_integration && hasCalendarProvider"
                    class="w-55 flex items-center justify-center gap-2"
                    :class="{ 'body-small font-medium whitespace-nowrap': !tonalThemeEnabled }"
                    theme="primary"
                    :disabled="!hasCalendarProvider || !isTimezoneSelected || isFetchingAuthUrl || calendarPollingActive"
                    @click="startCalendarIntegration()">
                    <LoadingSpinner v-if="isFetchingAuthUrl || calendarPollingActive" class="w-4 h-4" />
                    <span>{{ calendarPollingActive ? "Connecting..." : "Confirm and Connect" }}</span>
                </BaseButton>
                <p v-else-if="!userDetails.has_calendar_integration && !hasCalendarProvider" class="body-small text-valence-grey-750 mt-2 text-center">
                    Calendar connection isn't available for your organization yet.
                </p>
                <BaseButton v-else class="w-50" :class="{ 'body-small font-medium': !tonalThemeEnabled }" theme="primary" :disabled="!isTimezoneSelected" @click="handleSave">
                    Save
                </BaseButton>
            </div>
        </div>
    </BaseDialog>
    <BaseToast v-model:open="isErrorToastOpen" theme="error">
        <template #description> <MarkdownText class="py-1" :text="errorToastMsgWithEmail" /> </template>
    </BaseToast>
</template>

<script setup>
import { Browser } from "@capacitor/browser";
import { router, usePage } from "@inertiajs/vue3";
import { useTimeoutPoll } from "@vueuse/core";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseSwitch from "~vue/components/BaseSwitch.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { isInIFrame } from "~vue/utils";
import { logUserInteraction } from "~vue/utils/logUtils";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, inject, onMounted, ref, watch } from "vue";

import BaseToast from "../BaseToast.vue";
import CalendarIntegrationDisconnect from "./CalendarIntegrationDisconnect.vue";

const isMobileApp = usePage().props.is_mobile_app;
// TODO: Remove the legacy calendar branch once the tonal theme becomes the default.

const emit = defineEmits(["close", "timezone-updated"]);
const userDetails = inject("user_details");
const calendarIntegration = inject("calendarIntegration");
// The connect flow builds /calendar_integration/<provider>/auth-url/ from the org's provider.
// If the org has no provider configured, that request is null (ENG-1423), so gate the connect
// affordance on a provider being present. The CALENDAR_INTEGRATION flag can be on without a
// provider (global default / rollout), so the flag alone is not a sufficient guard.
const hasCalendarProvider = computed(() => Boolean(calendarIntegration?.calendar_provider));
const selectedTimezone = ref(null);
const allowPrivateEvents = ref(calendarIntegration?.allow_private_events ?? !calendarIntegration?.private_toggle_enabled);
const updatePrivateEventPayload = computed(() => ({
    allow_private_events: !!allowPrivateEvents.value,
}));

const authUrl = computed(() => {
    const currentPath = window.location.pathname;
    const nextParam = currentPath !== "/" ? `&next=${encodeURIComponent(currentPath)}` : "";
    return isInIFrame()
        ? `/calendar_integration/${calendarIntegration?.calendar_provider}/auth-url/?teams=true&allow_private_events=${allowPrivateEvents.value}${nextParam}`
        : `/calendar_integration/${calendarIntegration?.calendar_provider}/auth-url/?allow_private_events=${allowPrivateEvents.value}${nextParam}`;
});
const { data: authData, execute: fetchAuthUrl, isFetching: isFetchingAuthUrl, onFetchError: onFetchingAuthError } = useFetch(authUrl, { immediate: false }).get().json();

// Refer to ADR 8 for more info about MS Teams Oauth for Calendar Integration
const { execute: checkStatus, data: statusData } = useFetch("/api/calendar_integration/status/", { immediate: false }).get().json();

const checkCalendarIntegrationComplete = async () => {
    try {
        await checkStatus();
        return statusData.value?.calendarIntegrationStatus || false;
    } catch {
        return false;
    }
};

const isErrorToastOpen = ref(false);
const errorToastMsg = ref("");
const errorToastMsgWithEmail = computed(() => `${errorToastMsg.value} If the issue persists please contact [hi@valence.co](mailto:hi@valence.co)`);
onFetchingAuthError(() => {
    isErrorToastOpen.value = true;
    errorToastMsg.value = "Encountered an error while trying to connect calendar, please try again.";
    logUserInteraction("calendar_integration_error", { calendar_error_type: "auth_url_fetch_error" });
});

const timezonePayload = computed(() => ({ timezone: selectedTimezone.value?.value }));
const { execute: updateTimezone } = useFetch("/api/accounts/user", { immediate: false }).put(timezonePayload).json();

const isTimezoneSelected = computed(() => selectedTimezone.value?.value != null);

const {
    resume: startPolling,
    pause: stopPolling,
    isActive: calendarPollingActive,
} = useTimeoutPoll(
    checkCalendarIntegrationComplete,
    2000, // Poll every 2 seconds
    {
        immediate: false,
        timeout: 40000, // 40 second timeout (20 polls * 2 seconds)
    },
);

watch(statusData, (data) => {
    if (data?.calendarIntegrationStatus === true) {
        stopPolling();
        if (isMobileApp) {
            Browser.close();
        }
        window.location.reload();
    }
});

const startCalendarIntegration = () => {
    updateUserTimezone();
    redirectForCalendarIntegration();
};

const redirectForCalendarIntegration = async () => {
    // Never build/fetch /calendar_integration/<provider>/auth-url/ without a provider (ENG-1423).
    if (!hasCalendarProvider.value) {
        return;
    }
    await fetchAuthUrl();

    if (authData.value?.auth_url) {
        if (isMobileApp) {
            // Mobile app: open in SFSafariViewController via Capacitor Browser plugin.
            // Poll for completion — the OAuth callback closes the browser automatically.
            Browser.open({ url: authData.value.auth_url });
            startPolling();
        } else if (isInIFrame()) {
            // Use magic login URL if available for Teams, otherwise fall back to auth URL
            const popupUrl = authData.value.magic_login_url || authData.value.auth_url;
            window.open(popupUrl, "_blank");
            startPolling();
        } else {
            // Standard web flow - redirect in same window
            window.location.href = authData.value.auth_url;
        }
    }
};

const updateUserTimezone = async () => {
    await updateTimezone();
    emit("timezone-updated");
    router.reload({ only: ["user_details"] });
};

const updateUserPrivateEvents = async () => {
    await updatePrivateEvents();
    router.reload({ only: ["user_details"] });
};

const handleSave = async () => {
    const promises = [];

    if (isTimezoneSelected.value) {
        promises.push(updateUserTimezone());
    }

    promises.push(updateUserPrivateEvents());

    await Promise.all(promises);
    closeDialog();
};

const {
    execute: updatePrivateEvents,
    onFetchResponse: onUpdatePrivateEventsResponse,
    onFetchError: onUpdatePrivateEventsError,
    data: privateEventsToggleState,
} = useFetch("/api/calendar_integration/credential/", { immediate: false }).patch(updatePrivateEventPayload).json();

onUpdatePrivateEventsResponse(() => {
    allowPrivateEvents.value = privateEventsToggleState.value.allow_private_events;
});

onUpdatePrivateEventsError(() => {
    errorToastMsg.value = "Encountered an error while updating private event settings. Please try again.";
    isErrorToastOpen.value = true;
    logUserInteraction("calendar_integration_error", { calendar_error_type: "private_event_toggle_error" });
});

const closeDialog = () => {
    emit("close");
};

onMounted(() => {
    if (userDetails.value.timezone) {
        selectedTimezone.value = { label: userDetails.value.timezone, value: userDetails.value.timezone };
    }
});
</script>

<style scoped>
:deep(.comboboxInput) {
    margin: 10px 20px;
}
</style>
